import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Avatar, Box, FormLabel, Typography } from '@mui/material';
import { isEqual, isEmpty } from 'lodash';
import { useMutation, gql } from 'urql';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserFormSchema, UserFormModel, User } from '../../../models/user';
import { getFirstAndLastName, getInitials } from '../../../utils';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ConfirmationModal from '../../promotions/ConfirmationModal';
import Loader from '../../Loader';

interface Props {
  user: User;
}

const SAVE_USER_DETAILS_MUTATION = gql`
  mutation SaveUserDetails($fields: UserInput!) {
    saveUserDetails(fields: $fields) {
      id
      email
      name
      phone
    }
  }
`;
export default function EditUserForm({ user }: Props) {
  const [valuesChanged, setValuesChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();
  const userInitial = getInitials(user.name);
  const { firstName, lastName } = getFirstAndLastName(user.name);

  const initialValues: UserFormModel = useMemo(
    () => ({
      userPhone: user.phone,
      userFName: firstName,
      userLName: lastName,
      userEmail: user.email,
    }),
    [user, firstName, lastName],
  );

  const formMethods = useForm({
    resolver: yupResolver(UserFormSchema),
    mode: 'onBlur',
    criteriaMode: 'all',
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = formMethods;

  // eslint-disable-next-line react-hooks/incompatible-library
  const currentValues = watch();

  const [, EditUserDetails] = useMutation(SAVE_USER_DETAILS_MUTATION);

  useEffect(() => {
    if (!isEqual(initialValues, currentValues)) {
      setValuesChanged(true);
    } else {
      setValuesChanged(false);
    }
  }, [currentValues, initialValues]);

  const handleCancelEditAccount = () => {
    if (valuesChanged) {
      setOpenModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const handleDiscardEditAccount = () => {
    navigate(-1);
  };

  const onSubmit = async (data: UserFormModel) => {
    setIsLoading(true);
    const fieldData = {
      id: user.id,
      companiesId: user.companiesId,
      email: data.userEmail,
      name: data.userLName
        ? `${data.userFName} ${data.userLName}`
        : data.userFName,
      phone: data.userPhone,
      stores: [],
      notifications: [],
    };
    const response = await EditUserDetails({ fields: fieldData });

    if (response.error) {
      toast.error(response.error.message);
    } else {
      toast.success('Account updated successfully');
      navigate('/account');
    }
    setIsLoading(false);
  };

  return (
    <FormProvider {...formMethods}>
      <Loader active={isLoading} />
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        gap="48px"
        marginX="10%"
      >
        <Avatar
          sx={{
            height: '72px',
            width: '72px',
            fontSize: '24px',
            fontWeight: 600,
            bgcolor: 'avatar.main',
          }}
        >
          {userInitial}
        </Avatar>

        <Box
          display="flex"
          justifyContent="space-between"
          width="480px"
          alignItems="center"
        >
          <Typography variant="body1" fontSize="18px">
            Account Information
          </Typography>
          <Box display="flex" gap="12px">
            <Button onClick={handleCancelEditAccount} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={!valuesChanged || !isValid || !isEmpty(errors)}
              form="edit-account-form"
              type="submit"
            >
              Save Changes
            </Button>
          </Box>
        </Box>
        <form id="edit-account-form" onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" width="480px" flexDirection="column" gap="16px">
            <Controller
              name="userFName"
              control={control}
              defaultValue={initialValues.userFName}
              render={({ field }) => (
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  <FormLabel
                    htmlFor="user-first-name"
                    sx={{ fontSize: '14px' }}
                  >
                    First name
                  </FormLabel>
                  <Input {...field} id="user-first-name" className="w-3/4" />
                </Box>
              )}
            />
            <Controller
              name="userLName"
              control={control}
              defaultValue={initialValues.userLName}
              render={({ field }) => (
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  <FormLabel htmlFor="user-last-name" sx={{ fontSize: '14px' }}>
                    Last name
                  </FormLabel>
                  <Input {...field} id="user-last-name" className="w-3/4" />
                </Box>
              )}
            />
            <Controller
              name="userEmail"
              control={control}
              defaultValue={initialValues.userEmail}
              render={({ field }) => (
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  <FormLabel htmlFor="user-email" sx={{ fontSize: '14px' }}>
                    Email
                  </FormLabel>
                  <Input {...field} id="user-email" className="w-3/4" />
                </Box>
              )}
            />
            <Controller
              name="userPhone"
              control={control}
              defaultValue={initialValues.userPhone}
              render={({ field }) => (
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  <FormLabel htmlFor="user-phone" sx={{ fontSize: '14px' }}>
                    Phone number
                  </FormLabel>
                  <Input {...field} id="user-phone" className="w-3/4" />
                </Box>
              )}
            />
          </Box>
        </form>
        <ConfirmationModal
          open={openModal}
          title="Changes will be discarded"
          content="You have made changes to your account. If you cancel it now, they will be discarded."
          cancelButtonText="Continue Editing"
          confirmButtonText="Discard Changes"
          confirmButtonVariant="destructive"
          onCancel={handleModalClose}
          onConfirm={handleDiscardEditAccount}
        />
      </Box>
    </FormProvider>
  );
}
